import { Component, inject, Injector, Input, OnInit, signal } from '@angular/core';
import { Department, DepartmentSiteProperties } from '../../../models/department-manager/department';
import { BaseModalComponent } from '@card-software/library';
import { SiteService } from '@card-software/nexus-utilities';
import { User } from '../../../models/user-manager/user';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserProxyService } from '../../../proxies/user.proxy.service';
import { DepartmentsProxyService } from '@app/proxies/departments.proxy.service';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Translation } from '@app/models/translation';
import { finalize } from 'rxjs';
import { ToastService } from '@app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-department-modal',
  templateUrl: './department-modal.component.html',
  styleUrl: './department-modal.component.scss',
})
export class DepartmentModalComponent extends BaseModalComponent<Department | null> implements OnInit {
  private readonly userProxyService = inject(UserProxyService);
  private readonly departmentProxyService = inject(DepartmentsProxyService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  submittedForm = false;
  departmentReference = '';

  users: User[] = [];
  membersToDisplay: User[] = [];
  managersToDisplay: User[] = [];
  membersSelected: User[] = [];
  managersSelected: User[] = [];
  members: FormControl<User | null> = new FormControl(null);
  managers: FormControl<User | null> = new FormControl(null);
  spanishTranslate: FormControl<boolean> = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  membersFormArray: FormArray<FormControl<string[] | null>> = new FormArray<FormControl<string[] | null>>([]);
  managersFormArray: FormArray<FormControl<string[] | null>> = new FormArray<FormControl<string[] | null>>([]);

  departmentFormGroup: FormGroup = new FormGroup({
    name: new FormGroup({
      en: new FormControl('', Validators.required),
      es: new FormControl(''),
    }),
    description: new FormGroup({
      en: new FormControl(''),
      es: new FormControl(''),
    }),
    members: this.membersFormArray,
    managers: this.managersFormArray,
  });

  @Input() deparment: Department | null = null;
  isLoading = 0;
  isUpdate = signal(false);

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    if (this.deparment) {
      this.isUpdate.set(true);
      this.setInitialData(this.deparment);
    }
    this.getAllUsers();
  }

  private getAllUsers() {
    this.isLoading++;
    this.userProxyService
      .getAllUsers()
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          if (!response.data) {
            return;
          }
          this.users = response.data;

          // Si estamos en modo edición, cargar los usuarios seleccionados del site actual
          if (this.isUpdate() && this.deparment) {
            const siteProps = this.getCurrentSiteProperties(this.deparment);
            if (siteProps && siteProps.members.length > 0) {
              this.loadSelectedMembers(siteProps.members);
            }
            if (siteProps && siteProps.managers.length > 0) {
              this.loadSelectedManagers(siteProps.managers);
            }
          }
        },
      });
  }

  private setInitialData(deparment: Department) {
    if (!deparment) return;

    this.departmentReference = deparment.id ?? '';
    this.patchDepartmentFormGroup();
  }

  private clearMissingTranslations(field: 'name' | 'description'): void {
    const langs: (keyof Translation)[] = ['es', 'en'];

    const translation = this.deparment?.[field];
    if (!translation) return;

    langs.forEach((lang) => {
      if (translation.missingTranslations?.includes(lang)) {
        translation[lang] = '';
      }
    });
  }

  patchDepartmentFormGroup() {
    const nameHasEs = !!this.deparment?.name?.es?.trim() && !this.deparment?.name?.missingTranslations?.includes('es');
    const descHasEs = !!this.deparment?.description?.es?.trim() && !this.deparment?.description?.missingTranslations?.includes('es');
    this.spanishTranslate.setValue(nameHasEs || descHasEs);

    this.clearMissingTranslations('name');
    this.clearMissingTranslations('description');

    this.departmentFormGroup.patchValue({
      name: this.deparment?.name ?? { en: '', es: '' },
      description: this.deparment?.description ?? { en: '', es: '' },
    });

    // Cargar members y managers del site actual
    if (this.deparment) {
      const siteProps = this.getCurrentSiteProperties(this.deparment);
      if (siteProps && siteProps.members.length > 0) {
        this.loadSelectedMembers(siteProps.members);
      }
      if (siteProps && siteProps.managers.length > 0) {
        this.loadSelectedManagers(siteProps.managers);
      }
    }
  }

  private getCurrentSiteProperties(department: Department): DepartmentSiteProperties | undefined {
    const siteId = SiteService.getSelectedSiteId();
    return department.siteProperties?.find((sp) => sp.siteId === siteId) ?? department.siteProperties?.[0];
  }

  private buildSiteProperties(): DepartmentSiteProperties[] {
    const currentSiteId = SiteService.getSelectedSiteId() ?? '';
    const currentEntry: DepartmentSiteProperties = {
      siteId: currentSiteId,
      members: this.membersSelected.map((user) => user.username!),
      managers: this.managersSelected.map((user) => user.username!),
    };
    const otherSites = (this.deparment?.siteProperties ?? []).filter((sp) => sp.siteId !== currentSiteId);
    return [...otherSites, currentEntry];
  }

  private loadSelectedMembers(memberUsernames: string[]) {
    this.membersSelected = this.users.filter((user) => memberUsernames.includes(user.username!));
  }

  private loadSelectedManagers(managerUsernames: string[]) {
    this.managersSelected = this.users.filter((user) => managerUsernames.includes(user.username!));
  }

  // Método para evitar duplicados
  private isUserAlreadySelected(user: User, selectedUsers: User[]): boolean {
    return selectedUsers.some((selectedUser) => selectedUser.username === user.username);
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  onSubmit() {
    this.submittedForm = true;

    const missingFields: string[] = [];
    if (this.departmentFormGroup.get('name.en')?.invalid) {
      missingFields.push(`${this.translate.instant('Name')} (EN)`);
    }
    if (this.managersSelected.length === 0) {
      missingFields.push(this.translate.instant('Departments_Table_Managers'));
    }

    if (missingFields.length > 0) {
      this.toast.warn(
        this.translate.instant('Common_Warning'),
        `${this.translate.instant('Common_Required_Fields')}: ${missingFields.join(', ')}`,
      );
      return;
    }

    const formValue = this.departmentFormGroup.value;
    const departmentData: Department = {
      id: this.isUpdate() ? this.departmentReference : undefined,
      name: {
        en: formValue.name.en,
        es: this.spanishTranslate.value ? formValue.name.es : '',
      },
      description: {
        en: formValue.description.en,
        es: this.spanishTranslate.value ? formValue.description.es : '',
      },
      siteProperties: this.buildSiteProperties(),
    };

    this.isLoading++;

    if (this.isUpdate()) {
      this.updateDepartment(departmentData);
    } else {
      this.createDepartment(departmentData);
    }
  }

  private createDepartment(department: Department) {
    this.departmentProxyService
      .createDepartment(department)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          this.isLoading--;
          if (response.success && response.data) {
            this.close({ action: 'save', data: response.data });
          }
        },
      });
  }

  private updateDepartment(department: Department) {
    this.departmentProxyService
      .updateDepartment(department)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.close({ action: 'save', data: department });
          }
        },
      });
  }

  removeMember(index: number) {
    this.membersSelected.splice(index, 1);
  }

  removeManager(index: number) {
    this.managersSelected.splice(index, 1);
  }

  onSelectMember(event: AutoCompleteSelectEvent) {
    const user = event.value as User;

    // Verificar que no esté ya seleccionado
    if (!this.isUserAlreadySelected(user, this.membersSelected)) {
      this.membersSelected.push(user);
    } else {
      this.toast.warn(this.translate.instant('Common_Warning'), this.translate.instant('Departments_Duplicate_Member'));
    }

    this.members.setValue(null);
  }

  onSelectManager(event: AutoCompleteSelectEvent) {
    const user = event.value as User;

    if (!this.isUserAlreadySelected(user, this.managersSelected)) {
      this.managersSelected.push(user);
    } else {
      this.toast.warn(this.translate.instant('Common_Warning'), this.translate.instant('Departments_Duplicate_Manager'));
    }

    this.managers.setValue(null);
  }

  async searchMember(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    this.membersToDisplay = this.users
      .filter(
        (user) => user.username?.toLowerCase().includes(text.toLowerCase()) && !this.isUserAlreadySelected(user, this.membersSelected),
      )
      .slice(0, 8)
      .map((user) => ({
        ...user,
        fieldToDisplay: user.username,
      }));
  }

  async searchManager(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    this.managersToDisplay = this.users
      .filter(
        (user) => user.username?.toLowerCase().includes(text.toLowerCase()) && !this.isUserAlreadySelected(user, this.managersSelected),
      )
      .slice(0, 8)
      .map((user) => ({
        ...user,
        fieldToDisplay: user.username,
      }));
  }
}
